// build: 2026-04-11
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import api from '../../api/axios';
import Commercials from './Commercials';
import Invoices from './Invoices';
import EnterpriseDetailPanel from '../../components/enterprise/EnterpriseDetailPanel';

const PAGE_SIZE = 15;

const ManageEnterprises = ({ source = 'ECMS' }) => {
  const navigate = useNavigate();
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterNoBilling, setFilterNoBilling] = useState(false);
  const [filterAccountType, setFilterAccountType] = useState('');
  const [page, setPage] = useState(1);

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState('');
  const [showSyncPicker, setShowSyncPicker] = useState(false);
  const [syncMonth, setSyncMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [syncYear, setSyncYear] = useState(new Date().getFullYear());

  // Detail modal state
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [detailTab, setDetailTab] = useState('commercials'); // 'commercials', 'invoices', or 'details'

  // Demo Period popover state: { enterpriseId, activatedAt, loading, open }
  const [demoPeriod, setDemoPeriod] = useState({});

  const fetchAll = () => {
    setLoading(true);
    api.get(`/api/admin/enterprises?source=${source}`)
      .then(res => setEnterprises(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, [source]);

  const handleDemoPeriodClick = async (e, enterpriseLocalId, enterpriseUpdatedAt) => {
    e.stopPropagation();
    // Toggle off if already open for this enterprise
    if (demoPeriod.enterpriseId === enterpriseLocalId && demoPeriod.open) {
      setDemoPeriod({});
      return;
    }
    setDemoPeriod({ enterpriseId: enterpriseLocalId, open: true, loading: true });
    try {
      const res = await api.get(`/api/admin/enterprises/${enterpriseLocalId}/commercials/history`);
      const history = res.data || [];
      const sorted = [...history].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      // History stores NEW values. Walk DESC to find oldest consecutive DEMO entry
      // (i.e. the entry that first set DEMO in the current unbroken streak).
      let activationEntry = null;
      for (const h of sorted) {
        if (h.accountType === 'DEMO') {
          activationEntry = h; // keep going — oldest DEMO in streak wins
        } else {
          break; // chain broken, stop
        }
      }
      const activatedAt = activationEntry ? new Date(activationEntry.createdAt) : new Date(enterpriseUpdatedAt);
      const isApprox = !activationEntry;
      setDemoPeriod({ enterpriseId: enterpriseLocalId, open: true, loading: false, activatedAt, isApprox });
    } catch {
      setDemoPeriod({ enterpriseId: enterpriseLocalId, open: true, loading: false, activatedAt: new Date(enterpriseUpdatedAt), isApprox: true });
    }
  };

  const handleSync = async () => {
    setSyncError('');
    setSyncResult(null);
    setSyncing(true);
    setShowSyncPicker(false);
    const fromDate = `${syncYear}-${String(syncMonth).padStart(2, '0')}-01`;
    try {
      const syncEndpoint = source === 'IFLEET'
        ? `/api/admin/enterprises/sync/ifleet?fromDate=${fromDate}`
        : `/api/admin/enterprises/sync?fromDate=${fromDate}`;
      const res = await api.post(syncEndpoint);
      setSyncResult(res.data);
      fetchAll();
    } catch (e) {
      setSyncError(e.response?.data?.error || 'Sync failed. Please try again.');
    } finally { setSyncing(false); }
  };

  const handleToggle = async (enterpriseId) => {
    await api.post(`/api/admin/enterprises/${enterpriseId}/toggle`);
    fetchAll();
  };

  // Billing start date edit state
  const [billingDateEdit, setBillingDateEdit] = useState({ open: false, value: '', saving: false, error: '' });

  const openBillingDateEdit = (enterprise) => {
    const current = enterprise.billingStartDate
      ? enterprise.billingStartDate.substring(0, 10)
      : (enterprise.createdOn ? enterprise.createdOn.substring(0, 10) : '');
    setBillingDateEdit({ open: true, value: current, saving: false, error: '' });
  };

  const handleBillingDateSave = async (enterprise) => {
    setBillingDateEdit(s => ({ ...s, saving: true, error: '' }));
    try {
      const newBillingDate = billingDateEdit.value || null;
      await api.post(`/api/admin/enterprises/${enterprise.enterpriseId}/billing-start-date`, {
        billingStartDate: newBillingDate,
      });
      setBillingDateEdit({ open: false, value: '', saving: false, error: '' });
      fetchAll();
      // Update selectedEnterprise immediately so details panel reflects change
      setSelectedEnterprise(prev => ({
        ...prev,
        billingStartDate: newBillingDate,
        effectiveBillingStartDate: newBillingDate || (prev.createdOn ? prev.createdOn.substring(0, 10) : null),
      }));
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update billing start date';
      setBillingDateEdit(s => ({ ...s, saving: false, error: msg }));
    }
  };

  // Filter by name/id/KAM, optional "no billing config" toggle, and account type
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return enterprises.filter(e => {
      const matchesSearch = !q ||
        (e.enterpriseName ?? '').toLowerCase().includes(q) ||
        (e.enterpriseId ?? '').toLowerCase().includes(q) ||
        (e.kams || []).some(k => (k.kamName ?? '').toLowerCase().includes(q));
      const matchesBillingFilter = !filterNoBilling || !e.hasBillingConfig;
      const matchesAccountType = !filterAccountType || e.accountType === filterAccountType;
      return matchesSearch && matchesBillingFilter && matchesAccountType;
    });
  }, [enterprises, search, filterNoBilling, filterAccountType]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const noBillingCount = enterprises.filter(e => !e.hasBillingConfig).length;

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="w-full px-4 py-8">

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">{source === 'IFLEET' ? 'iFleet Enterprises' : 'ECMS Enterprises'}</h1>
          <p className="text-gray-500 text-sm">{enterprises.length} enterprises total</p>
        </div>

        {/* Sync Enterprises */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-700">Sync Enterprises</h2>
              <p className="text-xs text-gray-400 mt-0.5">{source === 'IFLEET' ? 'Fetches latest iFleet enterprise data from fleet_mngt_workspace.' : 'Fetches latest enterprise data from ORID and updates local records.'}</p>
            </div>
            <button onClick={() => setShowSyncPicker(p => !p)} disabled={syncing}
              className="bg-primary text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-primary-light disabled:opacity-50 transition-colors flex items-center gap-2">
              {syncing ? (
                <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"></span> Syncing...</>
              ) : (
                '⟳ Sync Enterprises'
              )}
            </button>
          </div>

          {/* Month / Year picker */}
          {showSyncPicker && (
            <div className="mt-4 flex items-end gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Month</label>
                <select
                  value={syncMonth}
                  onChange={e => setSyncMonth(Number(e.target.value))}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {['January','February','March','April','May','June','July','August','September','October','November','December']
                    .map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <input
                  type="number"
                  value={syncYear}
                  onChange={e => setSyncYear(Number(e.target.value))}
                  min="2020"
                  max={new Date().getFullYear()}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <button
                onClick={handleSync}
                className="bg-primary text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-primary-light transition-colors"
              >
                Start Sync
              </button>
              <button
                onClick={() => setShowSyncPicker(false)}
                className="text-sm text-gray-400 hover:text-gray-600 px-2 py-1.5"
              >
                Cancel
              </button>
            </div>
          )}

          {syncError && <p className="text-xs text-red-500 mt-3">{syncError}</p>}
          {syncResult && (
            <div className="mt-3 flex gap-4 text-xs">
              <span className="text-green-600 font-medium">✓ {syncResult.inserted} inserted</span>
              <span className="text-blue-600 font-medium">↻ {syncResult.updated} updated</span>
              <span className="text-gray-400">— {syncResult.skipped} skipped</span>
              <span className="text-gray-500">({syncResult.total} total from ORID)</span>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

          {/* Search + filter + count */}
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
            <div>
              <p className="text-sm font-semibold text-gray-700">All Enterprises</p>
              <p className="text-xs text-gray-400">{filtered.length} of {enterprises.length} shown</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Account Type filter */}
              <select
                value={filterAccountType}
                onChange={e => { setFilterAccountType(e.target.value); setPage(1); }}
                className={`border rounded-lg px-3 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${
                  filterAccountType
                    ? 'border-blue-300 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-gray-50 text-gray-500'
                }`}
              >
                <option value="">All Account Types</option>
                <option value="DEMO">Demo Account</option>
                <option value="ZEEROW">Zeerow Account</option>
                <option value="TESTING">Testing Account</option>
                <option value="INCONSISTENT">Inconsistent Commercial</option>
                <option value="ANNUAL">Annual Charges</option>
                <option value="CLOSED">Closed Account</option>
              </select>

              {/* No Billing Config filter */}
              <button
                onClick={() => { setFilterNoBilling(f => !f); setPage(1); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  filterNoBilling
                    ? 'bg-orange-50 text-orange-600 border-orange-300'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${filterNoBilling ? 'bg-orange-500' : 'bg-gray-300'}`}></span>
                No Billing Config
                {noBillingCount > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                    filterNoBilling ? 'bg-orange-100 text-orange-700' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {noBillingCount}
                  </span>
                )}
              </button>
              <input
                type="text"
                value={search}
                onChange={handleSearch}
                placeholder="Search by enterprise or KAM name..."
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
          ) : paged.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">No enterprises found.</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase bg-gray-50">
                  <th className="text-left px-5 py-3 font-medium whitespace-nowrap">ID</th>
                  <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Enterprise Name</th>
                  <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Created On</th>
                  <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Billing Start</th>
                  <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Assigned KAMs</th>
                  <th className="text-center px-5 py-3 font-medium whitespace-nowrap">Billing Config</th>
                  <th className="text-center px-5 py-3 font-medium whitespace-nowrap">Account Type</th>
                  <th className="text-center px-5 py-3 font-medium whitespace-nowrap">Status</th>
                  <th className="text-right px-5 py-3 font-medium whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paged.map(e => (
                  <tr key={e.enterpriseId} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-mono text-xs text-gray-400 max-w-[160px] truncate" title={e.enterpriseId}>{e.enterpriseId}</td>
                    <td className="px-5 py-3 font-medium text-gray-800">{e.enterpriseName}</td>
                    <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(e.createdOn)}</td>
                    <td className="px-5 py-3 text-xs whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {e.billingStartDate
                          ? <span className="text-orange-600 font-medium" title="Custom billing start date">{formatDate(e.billingStartDate)}</span>
                          : <span className="text-gray-400">{formatDate(e.effectiveBillingStartDate)}</span>}
                        <button
                          title="Edit billing start date"
                          onClick={() => {
                            setSelectedEnterprise(e);
                            setDetailTab('details');
                            const current = e.billingStartDate
                              ? e.billingStartDate.substring(0, 10)
                              : (e.createdOn ? e.createdOn.substring(0, 10) : '');
                            setBillingDateEdit({ open: true, value: current, saving: false, error: '' });
                          }}
                          className="text-gray-300 hover:text-blue-500 transition-colors flex-shrink-0"
                        >
                          ✎
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {(e.kams || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {e.kams.map(k => (
                            <span key={k.kamId} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                              {k.kamName || k.kamId}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No KAM assigned</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {e.hasBillingConfig ? (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                          Configured
                        </span>
                      ) : (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">
                          Not Set
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {e.accountType ? (
                        <div className="inline-flex flex-col items-center gap-1">
                          <span className={`inline-flex flex-col items-center text-xs font-semibold px-3 py-1.5 rounded-lg leading-snug ${
                            e.accountType === 'DEMO'        ? 'bg-blue-100 text-blue-700' :
                            e.accountType === 'ZEEROW'      ? 'bg-purple-100 text-purple-700' :
                            e.accountType === 'TESTING'     ? 'bg-orange-100 text-orange-700' :
                            e.accountType === 'ANNUAL'      ? 'bg-green-100 text-green-700' :
                            e.accountType === 'CLOSED'      ? 'bg-gray-200 text-gray-600' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {e.accountType === 'DEMO'    ? <><span>Demo</span><span>Account</span></> :
                             e.accountType === 'ZEEROW'  ? <><span>Zeerow</span><span>Account</span></> :
                             e.accountType === 'TESTING' ? <><span>Testing</span><span>Account</span></> :
                             e.accountType === 'ANNUAL'  ? <><span>Annual</span><span>Charges</span></> :
                             e.accountType === 'CLOSED'  ? <><span>Closed</span><span>Account</span></> :
                             <><span>Inconsistent</span><span>Commercials</span></>}
                          </span>

                          {/* Demo Period button */}
                          {e.accountType === 'DEMO' && (
                            <div className="relative">
                              <button
                                type="button"
                                onClick={(ev) => handleDemoPeriodClick(ev, e.id, e.commercialUpdatedAt)}
                                className="text-xs text-blue-600 font-medium underline underline-offset-2 hover:text-blue-800 transition-colors"
                              >
                                Demo Period
                              </button>
                              {demoPeriod.enterpriseId === e.id && demoPeriod.open && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setDemoPeriod({})} />
                                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-20 bg-white border border-blue-200 rounded-xl shadow-lg px-4 py-3 w-56">
                                  {demoPeriod.loading ? (
                                    <p className="text-xs text-gray-400">Loading...</p>
                                  ) : demoPeriod.activatedAt ? (() => {
                                    const days = Math.floor((new Date() - demoPeriod.activatedAt) / (1000 * 60 * 60 * 24));
                                    return (
                                      <div className="space-y-1">
                                        <p className="text-xs text-gray-500">
                                          Since{demoPeriod.isApprox ? ' (approx.)' : ''}
                                        </p>
                                        <p className="text-sm font-semibold text-gray-800">
                                          {demoPeriod.activatedAt.toLocaleDateString('en-IN', {
                                            day: '2-digit', month: 'short', year: 'numeric'
                                          })}
                                        </p>
                                        <p className="text-xs font-medium text-blue-600">
                                          {days === 0 ? 'Started today' : `${days} day${days > 1 ? 's' : ''} ago`}
                                        </p>
                                      </div>
                                    );
                                  })() : (
                                    <p className="text-xs text-gray-400">Date not available</p>
                                  )}
                                </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        e.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {e.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {e.accountType === 'ZEEROW' ? (
                        <button
                          onClick={() => navigate('/admin/zeerow/config')}
                          className="text-xs bg-purple-50 text-purple-600 border border-purple-200 px-3 py-1 rounded-lg hover:bg-purple-100 transition-colors">
                          → Zeerow
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedEnterprise(e);
                            setDetailTab('commercials');
                          }}
                          className="text-xs bg-blue-50 text-blue-600 border border-blue-200 px-3 py-1 rounded-lg hover:bg-blue-100 transition-colors">
                          Manage
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Page {page} of {totalPages} &middot; {filtered.length} enterprises
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors">
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                  const p = start + i;
                  return p <= totalPages ? (
                    <button key={p} onClick={() => setPage(p)}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                        p === page ? 'bg-primary text-white border-primary' : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                      {p}
                    </button>
                  ) : null;
                })}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Detail Modal */}
      {selectedEnterprise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-7xl max-h-screen overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">{selectedEnterprise.enterpriseName}</h2>
                <p className="text-xs text-gray-400 mt-1">{selectedEnterprise.enterpriseId}</p>
              </div>
              <button
                onClick={() => { setSelectedEnterprise(null); fetchAll(); }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
                ×
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-100 px-6">
              <div className="flex gap-8">
                <button
                  onClick={() => setDetailTab('commercials')}
                  className={`py-3 text-sm font-medium transition-colors border-b-2 ${
                    detailTab === 'commercials'
                      ? 'text-primary border-primary'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  }`}>
                  Billing Configuration
                </button>
                <button
                  onClick={() => setDetailTab('invoices')}
                  className={`py-3 text-sm font-medium transition-colors border-b-2 ${
                    detailTab === 'invoices'
                      ? 'text-primary border-primary'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  }`}>
                  Billing
                </button>
                <button
                  onClick={() => setDetailTab('details')}
                  className={`py-3 text-sm font-medium transition-colors border-b-2 ${
                    detailTab === 'details'
                      ? 'text-primary border-primary'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  }`}>
                  Enterprise Details
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {detailTab === 'commercials' && (
                <Commercials enterpriseId={selectedEnterprise.id} onSave={fetchAll} createdOn={selectedEnterprise.createdOn} />
              )}
              {detailTab === 'invoices' && (
                <Invoices enterpriseId={selectedEnterprise.id} />
              )}
              {detailTab === 'details' && (
                <EnterpriseDetailPanel
                  enterprise={selectedEnterprise}
                  source={source}
                  onBillingDateSaved={fetchAll}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageEnterprises;
